/**
 * 크로스블록 편집 컨트롤러(Phase 2) — 크로스 선택이 활성일 때 키 입력을 가로채 선택 영역을 편집한다.
 *
 * 다른 keydown 어태처(editing/caret-nav/block-select)보다 **먼저** 등록해, 처리 시
 * stopImmediatePropagation으로 하위 핸들러의 단일 블록 동작을 막는다.
 *
 *  - Backspace/Delete → 선택 삭제(deleteRangeAcross) + 커밋 1회(단일 undo).
 *  - Enter → 선택 삭제 후 join 지점에서 블록 분할(handleEnter 재사용).
 *  - 인쇄 가능한 문자 → 선택 삭제 후 그 자리에 문자 삽입(모델 삽입).
 *  - Ctrl/Cmd+C·X → 부분 직렬화 클립보드 기록(cut은 기록 성공 후 삭제).
 *  - 그 외 Ctrl/Cmd 조합(B/I/U 등)·화살표·Escape는 통과(각각 툴바/크로스 선택 컨트롤러가 처리).
 *  - 한글 등 IME 입력은 여기서 처리하지 않는다(compositionstart 경로 — editor.js).
 *
 * 표/단/중첩 경계 선택은 deleteRangeAcross가 null을 반환(v1 제외)하므로 삭제/타이핑이 no-op이 되고,
 * 이 경우에도 stopImmediatePropagation을 하므로 잘못된 단일 블록 편집이 일어나지 않는다.
 */

import { deleteRangeAcross, buildCrossBlockClipboard } from "./cross-block-range.js";
import { embedBlocksInHtml } from "./block-select.js";
import { handleEnter } from "../editing.js";
import { setSelection, contentToChars, charsToHtml } from "../inline-format.js";

export function attachCrossBlockEdit(editorEl, opts) {
  opts = opts || {};
  const isEditable = typeof opts.isEditable === "function" ? opts.isEditable : () => true;
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const commitChange = typeof opts.commitChange === "function" ? opts.commitChange : () => {};
  const getSel = typeof opts.getSelection === "function" ? opts.getSelection : () => null;
  const isActive = typeof opts.isActive === "function" ? opts.isActive : () => false;
  const clearCross = typeof opts.clearCross === "function" ? opts.clearCross : () => {};
  const getDocument = typeof opts.getDocument === "function" ? opts.getDocument : () => [];
  const jsonOf = typeof opts.jsonOf === "function" ? opts.jsonOf : () => "";
  const htmlOf = typeof opts.htmlOf === "function" ? opts.htmlOf : () => "";
  const textOf = typeof opts.textOf === "function" ? opts.textOf : () => "";

  function placeCaret(caret) {
    if (!caret || !caret.island) return;
    try {
      caret.island.focus();
      setSelection(caret.island, caret.offset, caret.offset);
    } catch (_) {}
  }
  function insertCharAt(caret, ch) {
    if (!caret || !caret.island) return;
    const chars = contentToChars(caret.island);
    const next = chars.slice(0, caret.offset).concat([{ ch, styles: {} }], chars.slice(caret.offset));
    caret.island.innerHTML = charsToHtml(next);
    try {
      caret.island.focus();
      setSelection(caret.island, caret.offset + 1, caret.offset + 1);
    } catch (_) {}
  }

  function copyCross(sel, cut) {
    const blocks = buildCrossBlockClipboard(editorEl, sel, getDocument());
    if (!blocks || !blocks.length) return;
    const json = jsonOf(blocks);
    const html = embedBlocksInHtml(json, htmlOf(blocks)); // 무손실 JSON을 html data 속성에 임베드
    const text = textOf(blocks);
    const doCut = () => {
      if (!cut) return;
      const caret = deleteRangeAcross(editorEl, sel, { collapseEmpty: true });
      clearCross();
      placeCaret(caret);
      commitChange(); // cut 삭제는 쓰기 성공 후에만(데이터 유실 방지)
    };
    try {
      if (
        typeof navigator !== "undefined" && navigator.clipboard &&
        typeof navigator.clipboard.write === "function" && typeof window.ClipboardItem === "function"
      ) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        navigator.clipboard.write([item]).then(doCut, () => {});
        return;
      }
    } catch (_) {}
    // 비동기 Clipboard 미지원 시: 크로스는 네이티브 range가 비어 execCommand 폴백이 부정확하므로 생략(무동작).
  }

  function onKeydown(e) {
    if (!isEditable() || !isActive() || isComposing() || e.isComposing) return;
    const sel = getSel();
    const mod = e.metaKey || e.ctrlKey;

    if (mod && !e.altKey && (e.key === "c" || e.key === "C" || e.key === "x" || e.key === "X")) {
      e.preventDefault();
      e.stopImmediatePropagation();
      copyCross(sel, e.key === "x" || e.key === "X");
      return;
    }
    if (mod) return; // B/I/U 등은 editing/툴바 경로로 통과
    if (e.altKey) return;

    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      e.stopImmediatePropagation();
      const caret = deleteRangeAcross(editorEl, sel, { collapseEmpty: true });
      clearCross();
      placeCaret(caret);
      commitChange();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const caret = deleteRangeAcross(editorEl, sel);
      clearCross();
      if (caret) {
        placeCaret(caret);
        handleEnter({ target: caret.island, preventDefault() {} }, () => {}); // join 지점 분할
      }
      commitChange();
      return;
    }
    // 인쇄 가능한 단일 문자(비-IME) → 선택 삭제 후 그 자리에 삽입.
    if (e.key && e.key.length === 1) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const caret = deleteRangeAcross(editorEl, sel);
      clearCross();
      if (caret) insertCharAt(caret, e.key);
      commitChange();
      return;
    }
  }

  editorEl.addEventListener("keydown", onKeydown);
  return {
    destroy() {
      editorEl.removeEventListener("keydown", onKeydown);
    },
  };
}
